import type { ConditionOp, ConditionalVisibility, FieldDef, FieldOption, VisibilityNode, VisibilityRule } from '@jfh/intake-shared';
import { isVisibilityGroup, visibilitySegments } from '@jfh/intake-shared';
import { Icon } from '../../components/Icon.js';
import { SearchSelect } from '../../components/SearchSelect.js';
import type { RuleCandidate } from './logic.js';
import { MiniButton } from './ui.js';

const NO_VALUE: ConditionOp[] = ['empty', 'not_empty'];

const CHOICE_OPS: [string, string][] = [
  ['is', 'is'],
  ['neq', 'is not'],
  ['not_empty', 'has been answered'],
  ['empty', 'has been left blank'],
];
const MULTI_OPS: [string, string][] = [
  ['contains', 'includes'],
  ['not_empty', 'has been answered'],
  ['empty', 'has been left blank'],
];
const TEXT_OPS: [string, string][] = [
  ['eq', 'is exactly'],
  ['neq', 'is not'],
  ['contains', 'includes'],
  ['gt', 'is more than'],
  ['lt', 'is less than'],
  ['not_empty', 'has been answered'],
  ['empty', 'has been left blank'],
];
const ALL_OP_LABELS: Record<string, string> = {
  eq: 'is',
  in: 'is one of (separate with commas)',
  neq: 'is not',
  contains: 'includes',
  gt: 'is more than',
  lt: 'is less than',
  not_empty: 'has been answered',
  empty: 'has been left blank',
};

/** Up to this many answers show as buttons; longer lists (e.g. countries) are searched. */
const MAX_PILLS = 12;

/**
 * A long answer list (an iQ list such as countries): the chosen answers as
 * chips, and a search box to add one (or, for `single`, to pick the one).
 */
function SearchPick({
  options,
  values,
  single,
  onToggle,
}: {
  options: FieldOption[];
  values: string[];
  single: boolean;
  onToggle: (value: string) => void;
}) {
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {values.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {values.map((v) => (
            <span
              key={v}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                height: 28,
                padding: '0 4px 0 10px',
                borderRadius: 99,
                fontSize: 12,
                fontWeight: 700,
                border: '1px solid var(--accent)',
                background: 'var(--accent-soft)',
                color: 'var(--accent-text)',
              }}
            >
              {labelOf(v)}
              <button
                type="button"
                aria-label={`Remove ${labelOf(v)}`}
                onClick={() => onToggle(v)}
                style={{ border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 2, display: 'flex' }}
              >
                <Icon name="x" size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
      <SearchSelect
        value=""
        options={options.filter((o) => !values.includes(o.value))}
        onChange={onToggle}
        placeholder={single ? (values.length ? 'Choose a different answer…' : 'Choose an answer…') : values.length ? 'Add another answer…' : 'Choose an answer…'}
        searchPlaceholder="Type to search"
        ariaLabel="Answer"
        style={{ height: 32, fontSize: 12.5 }}
      />
    </div>
  );
}

function LeafEditor({
  rule,
  candidates,
  onChange,
  onRemove,
}: {
  rule: VisibilityRule;
  candidates: RuleCandidate[];
  onChange: (next: VisibilityRule) => void;
  onRemove: () => void;
}) {
  const drv = candidates.find((c) => c.id === rule.fieldId);
  const isChoice = Boolean(drv?.options) && !drv?.multi;
  const isMulti = Boolean(drv?.options) && Boolean(drv?.multi);
  const values =
    rule.op === 'in'
      ? String(rule.value ?? '').split(',').map((v) => v.trim()).filter(Boolean)
      : rule.value
        ? [rule.value]
        : [];
  const baseOps = isChoice ? CHOICE_OPS : isMulti ? MULTI_OPS : TEXT_OPS;
  const selectedOp = isChoice && (rule.op === 'eq' || rule.op === 'in') ? 'is' : rule.op;
  // Keep an existing operator visible even if it isn't one we'd offer for this
  // kind of question, so nothing is lost by opening the rule.
  const ops = baseOps.some(([v]) => v === selectedOp)
    ? baseOps
    : [...baseOps, [selectedOp, ALL_OP_LABELS[selectedOp] ?? selectedOp] as [string, string]];
  const needsValue = !NO_VALUE.includes(rule.op);

  const setOp = (v: string) => {
    if (v === 'is') onChange({ ...rule, op: values.length > 1 ? 'in' : 'eq' });
    else if (v === 'neq' || v === 'contains') onChange({ ...rule, op: v as ConditionOp, value: values[0] ?? '' });
    else onChange({ ...rule, op: v as ConditionOp });
  };

  const togglePill = (val: string) => {
    if (rule.op === 'neq' || isMulti) {
      onChange({ ...rule, op: isMulti ? 'contains' : 'neq', value: val });
      return;
    }
    const on = values.includes(val);
    const next = on ? values.filter((x) => x !== val) : [...values, val];
    onChange({ ...rule, op: next.length > 1 ? 'in' : 'eq', value: next.join(',') });
  };

  return (
    <div
      style={{
        display: 'grid',
        gap: 6,
        padding: 10,
        borderRadius: 'var(--r-md)',
        background: drv ? 'var(--bg-surface-2)' : 'var(--danger-100)',
        border: `1px solid ${drv ? 'var(--border)' : 'var(--st-declined)'}`,
      }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <select
          className="select"
          style={{ height: 32, fontSize: 12.5, flex: 1, minWidth: 0 }}
          value={rule.fieldId}
          aria-label="Question"
          onChange={(e) => onChange({ fieldId: e.target.value, op: 'eq', value: '' })}
        >
          {!drv && <option value={rule.fieldId}>(deleted question)</option>}
          {candidates.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
        <MiniButton label="Remove condition" onClick={onRemove}>
          <Icon name="trash" size={13} />
        </MiniButton>
      </div>
      <select
        className="select"
        style={{ height: 32, fontSize: 12.5 }}
        value={selectedOp}
        aria-label="Condition"
        onChange={(e) => setOp(e.target.value)}
      >
        {ops.map(([v, label]) => (
          <option key={v} value={v}>
            {label}
          </option>
        ))}
      </select>
      {needsValue && drv?.options && (
        <>
          {drv.options.length > MAX_PILLS ? (
            <SearchPick
              options={drv.options}
              values={values}
              single={rule.op === 'neq' || isMulti}
              onToggle={togglePill}
            />
          ) : (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {drv.options.map((o) => {
                  const on = values.includes(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={on}
                      onClick={() => togglePill(o.value)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        height: 28,
                        padding: '0 10px',
                        borderRadius: 99,
                        fontSize: 12,
                        fontWeight: 700,
                        border: `1px solid ${on ? 'var(--accent)' : 'var(--border-strong)'}`,
                        background: on ? 'var(--accent-soft)' : 'var(--bg-surface)',
                        color: on ? 'var(--accent-text)' : 'var(--text-2)',
                      }}
                    >
                      {on && <Icon name="check" size={12} />}
                      {o.label}
                    </button>
                  );
                })}
              </div>
          )}
          <div className="field-help" style={{ marginTop: 0 }}>
            {rule.op === 'neq' || isMulti ? 'Pick one answer.' : 'Pick one or more. Any of them will show it.'}
          </div>
        </>
      )}
      {needsValue && !drv?.options && (
        <input
          className="input"
          style={{ height: 32, fontSize: 12.5 }}
          value={rule.value ?? ''}
          placeholder={rule.fieldId.endsWith('.country') ? 'Country code, e.g. GB' : 'Type the answer'}
          aria-label="Answer"
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
        />
      )}
      {!drv && (
        <div style={{ fontSize: 12, color: 'var(--st-declined)', fontWeight: 700 }}>
          This used a question that has been deleted. Pick another or remove the condition.
        </div>
      )}
    </div>
  );
}

function JoinWord({ word }: { word: string }) {
  return (
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: '.1em',
        color: 'var(--text-3)',
        textTransform: 'uppercase',
        margin: '0 0 6px 2px',
      }}
    >
      {word}
    </div>
  );
}

function MatchSwitch({ match, onChange }: { match: 'all' | 'any'; onChange: (m: 'all' | 'any') => void }) {
  return (
    <div style={{ display: 'flex', gap: 3, background: 'var(--bg-sunken)', padding: 2, borderRadius: 6 }}>
      {(['all', 'any'] as const).map((m) => (
        <button
          key={m}
          type="button"
          aria-pressed={match === m}
          onClick={() => onChange(m)}
          style={{
            border: 'none',
            fontSize: 12,
            fontWeight: 700,
            padding: '3px 9px',
            borderRadius: 4,
            background: match === m ? 'var(--bg-surface)' : 'transparent',
            color: 'var(--text-1)',
          }}
        >
          {m === 'all' ? 'all of these' : 'any of these'}
        </button>
      ))}
    </div>
  );
}

/**
 * "Only show this question when…" (F9 P2): the rule as a sentence, then one
 * editor per condition with answer pills. Existing nested groups display and
 * edit; there's deliberately no button to create new ones (Hayden, 22/10).
 */
export function RuleSentence({
  field,
  fields,
  candidates,
  onChange,
}: {
  field: FieldDef;
  fields: FieldDef[];
  candidates: RuleCandidate[];
  onChange: (next: ConditionalVisibility | undefined) => void;
}) {
  // A step's rule shows or skips the whole page.
  const step = field.type === 'heading';
  const raw = field.visibleWhen as ConditionalVisibility | undefined;
  const vis: ConditionalVisibility = { match: raw?.match ?? 'all', rules: raw?.rules ?? [] };
  const has = vis.rules.length > 0;
  const set = (next: ConditionalVisibility) => onChange(next.rules.length ? next : undefined);
  const newRule = (): VisibilityRule => {
    const prefer = candidates.find((c) => c.options && !c.multi) ?? candidates[0];
    return { fieldId: prefer?.id ?? '', op: 'eq', value: '' };
  };
  const setNode = (i: number, node: VisibilityNode) =>
    set({ ...vis, rules: vis.rules.map((r, j) => (j === i ? node : r)) });
  const removeNode = (i: number) => set({ ...vis, rules: vis.rules.filter((_, j) => j !== i) });

  // Answers read as their labels (a country's name, not its code).
  const labelOf = (id: string, value: string) =>
    candidates.find((c) => c.id === id)?.options?.find((o) => o.value === value)?.label;
  const segments = has ? visibilitySegments(vis, fields, labelOf) : [];

  return (
    <section
      style={{
        display: 'grid',
        gap: 10,
        padding: 14,
        background: 'var(--bg-surface)',
        border: `1px solid ${has ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--r-lg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--accent-text)' }}>
          <Icon name="filter" size={14} />
        </span>
        <span style={{ fontWeight: 800, fontSize: 13.5, flex: 1 }}>
          {step
            ? has
              ? 'Only show this step when…'
              : 'Show this step to everyone?'
            : has
              ? 'Only show this question when…'
              : 'Show this question to everyone?'}
        </span>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2)' }}>
        {has ? (
          <>
            {step ? 'Show this step when' : 'Show this question when'}{' '}
            {segments.map((s, i) =>
              s.strong ? (
                <strong key={i} style={{ color: 'var(--text-1)' }}>
                  {s.text}
                </strong>
              ) : (
                <span key={i}>{s.text}</span>
              ),
            )}
            .{step && ' Otherwise applicants skip it, with all its questions.'}
          </>
        ) : step ? (
          'Every applicant sees this step.'
        ) : (
          'Always shown to every applicant.'
        )}
      </div>

      {vis.rules.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)', flexWrap: 'wrap' }}>
          <span>Show it when</span>
          <MatchSwitch match={vis.match} onChange={(m) => set({ ...vis, match: m })} />
          <span>are true</span>
        </div>
      )}

      {vis.rules.map((node, i) => {
        const join = i ? (vis.match === 'any' ? 'or' : 'and') : '';
        if (isVisibilityGroup(node)) {
          return (
            <div key={i}>
              {join && <JoinWord word={join} />}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: 'var(--text-2)',
                  padding: '6px 8px',
                  border: '1px dashed var(--border-strong)',
                  borderRadius: 'var(--r-md)',
                  marginBottom: 6,
                }}
              >
                <span style={{ fontWeight: 700 }}>Group: when</span>
                <select
                  className="select"
                  style={{ height: 28, width: 'auto', fontSize: 12, padding: '0 6px' }}
                  value={node.match}
                  onChange={(e) => setNode(i, { ...node, match: e.target.value as 'all' | 'any' })}
                >
                  <option value="all">all</option>
                  <option value="any">any</option>
                </select>
                <span style={{ flex: 1 }}>of these are true</span>
                <button
                  type="button"
                  className="btn ghost sm"
                  onClick={() => setNode(i, { ...node, rules: [...node.rules, newRule()] })}
                >
                  <Icon name="plus" size={12} /> Condition
                </button>
                <MiniButton label="Remove group" onClick={() => removeNode(i)}>
                  <Icon name="x" size={13} />
                </MiniButton>
              </div>
              <div style={{ display: 'grid', gap: 6, paddingLeft: 14 }}>
                {node.rules.map((r, j) => (
                  <div key={j}>
                    {j > 0 && <JoinWord word={node.match === 'any' ? 'or' : 'and'} />}
                    {isVisibilityGroup(r) ? (
                      <div className="field-help" style={{ marginTop: 0 }}>
                        A deeper group. It still works; edit it by removing and re-adding its conditions.
                      </div>
                    ) : (
                      <LeafEditor
                        rule={r}
                        candidates={candidates}
                        onChange={(next) =>
                          setNode(i, { ...node, rules: node.rules.map((x, k) => (k === j ? next : x)) })
                        }
                        onRemove={() => setNode(i, { ...node, rules: node.rules.filter((_, k) => k !== j) })}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key={i}>
            {join && <JoinWord word={join} />}
            <LeafEditor
              rule={node}
              candidates={candidates}
              onChange={(next) => setNode(i, next)}
              onRemove={() => removeNode(i)}
            />
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn sm"
          disabled={!candidates.length}
          onClick={() => set({ ...vis, rules: [...vis.rules, newRule()] })}
        >
          <Icon name="plus" size={13} /> {has ? 'Add another condition' : 'Only show it when…'}
        </button>
        {has && (
          <button type="button" className="btn ghost sm" onClick={() => onChange(undefined)}>
            Always show it
          </button>
        )}
      </div>
    </section>
  );
}
